import { registerPlugin, WebPlugin } from '@capacitor/core';

/**
 * Die Bildschirmhelligkeit des Geräts.
 *
 * **Warum es das gibt.** Ein Prüfungssaal ist kein Schreibtisch: am Fenster blendet das
 * Gegenlicht, im Innenraum leuchtet ein Vollbild dem Prüfling ins Gesicht, und über acht
 * Stunden entscheidet die Helligkeit über die Ladung eines iPad 6. Die Aufsicht spannt in den
 * iPad-Einstellungen den Rahmen (`min_brightness`, `max_brightness`), der Benutzer wählt
 * darin — mit dem einen Regler in `features/helligkeit/`.
 *
 * ⚠️ **Alles in Prozent.** `UIScreen.main.brightness` rechnet in 0…1; geteilt wird nur im
 * Swift-Teil, an einer Stelle. Überall sonst steht eine ganze Zahl.
 *
 * ⚠️ **Die App stellt beim Verlassen nichts zurück** (Entscheidung des Nutzers, 2026-09-17).
 * Was der Benutzer wählt, gilt systemweit weiter, nachdem die App zu ist — iOS kennt hier
 * keinen Rückfall. Damit das niemanden überrascht, rührt die App die Helligkeit beim Start
 * auch **nicht von selbst** an: erst der Griff an den Regler schreibt. Ein iPad kommt also
 * nur dunkel aus dem Saal zurück, wenn jemand es dunkel gestellt hat.
 *
 * ⚠️ **Das ist nicht die Schlafscheibe** (`features/schlaf/`). Die legt sich nach fünf Minuten
 * Ruhe über die App und macht mit `backdrop-filter` Farbflächen aus dem Bogen — ein Vorhang
 * innerhalb der App. Hier geht es um die Hintergrundbeleuchtung des Geräts.
 */

export interface HelligkeitPlugin {
  /** Was gerade gilt, in Prozent. */
  lies(): Promise<{ wert: number }>;
  /** Setzt die Helligkeit; gibt zurück, was tatsächlich gilt. */
  setze(options: { wert: number }): Promise<{ wert: number }>;
}

/**
 * Im Browser gibt es keine Bildschirmhelligkeit, an die wir kämen.
 *
 * ⚠️ **Bewusst kein CSS-Filter, der Helligkeit vortäuscht.** Dieselbe Begründung wie bei
 * `wach.ts`: ein zweiter Weg, der nur im Entwicklungsbrowser greift, verdeckt am Gerät genau
 * den Fall, den er beheben soll. Der Ersatz merkt sich die Zahl und gibt sie zurück, mehr
 * nicht — damit bleibt die Oberfläche prüfbar, ohne etwas vorzuspiegeln.
 */
class HelligkeitWeb extends WebPlugin implements HelligkeitPlugin {
  private wert = 100;
  async lies(): Promise<{ wert: number }> {
    return { wert: this.wert };
  }
  async setze(options: { wert: number }): Promise<{ wert: number }> {
    this.wert = Math.min(100, Math.max(0, Math.round(options.wert)));
    return { wert: this.wert };
  }
}

export const Helligkeit = registerPlugin<HelligkeitPlugin>('Helligkeit', {
  web: () => new HelligkeitWeb(),
});

/* ------------------------------------------------------- Das Gedächtnis der Sitzung */

/**
 * Was der Benutzer gewählt hat — **kein** Katalogeintrag.
 *
 * Die Wahl geht nicht über QR, MDM oder die iPad-Einstellungen und liegt nicht in der
 * App-Datenbank. Im Katalog stehen nur die beiden Grenzen; alles zwischen ihnen ist
 * Sitzungszustand.
 *
 * ⚠️ **`sessionStorage`, nicht `localStorage`** (Vorgabe des Nutzers, 2026-09-17). Minimieren
 * und Zurückholen bringt den Stand wieder, ein vollständiger Neustart nicht — dann steht der
 * Regler wieder dort, wo das Gerät gerade steht.
 *
 * ⚠️ **iOS kann eine minimierte App bei Speicherdruck beenden**
 * (`memory/state-restoration.md`). Dann ist das Zurückholen in Wahrheit ein Kaltstart: der
 * WebView lädt neu, dieser Speicher ist leer. Das ist nicht abzuwenden und nach der Regel des
 * Nutzers auch richtig.
 */
const SITZUNG = 'ui.helligkeit';

export function liesSitzung(): number | null {
  const roh = sessionStorage.getItem(SITZUNG);
  if (roh === null) return null;
  const n = Number(roh);
  // Fremder Inhalt darf keinen NaN in den Regler lassen — ein `value={NaN}` an einem
  // gesteuerten Feld macht daraus ein ungesteuertes, und React sagt es nur als Warnung.
  return Number.isFinite(n) ? n : null;
}

export function merkeSitzung(wert: number): void {
  sessionStorage.setItem(SITZUNG, String(wert));
}

/** Nur für Tests: den gemerkten Stand vergessen. */
export function vergissSitzung(): void {
  sessionStorage.removeItem(SITZUNG);
}
